import React, { useState } from "react";
import { Box, Button, Stack, Textarea, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type {
  ActionRequest,
  AssistantStep,
  Decision,
  PendingApprovalData,
  ReviewConfig,
} from "./types";

// =========================
// ApprovalDecisionStep — 사용자가 내린 결정 표시
// =========================

export function ApprovalDecisionRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "approval_decision" }>;
}) {
  const decisionLabel =
    step.decision.type === "approve"
      ? "✓ 승인"
      : step.decision.type === "edit"
        ? "✎ 수정 후 적용"
        : "✕ 거부";
  const decisionColor =
    step.decision.type === "approve"
      ? "#15803d"
      : step.decision.type === "edit"
        ? theme.vars.palette.primary.solid
        : "#b91c1c";
  const rejectMessage =
    step.decision.type === "reject" ? step.decision.message : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: decisionColor,
          fontWeight: 700,
        }}
      >
        {decisionLabel}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {step.originalAction.name}
      </Box>
      {rejectMessage && (
        <Box
          component="span"
          sx={{
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          “{rejectMessage}”
        </Box>
      )}
    </Box>
  );
}

// =========================
// ApprovalCard
// =========================

export function ApprovalCard({
  data,
  onDecide,
}: {
  data: PendingApprovalData;
  onDecide: (decisions: Decision[]) => void;
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: "12px",
        mt: "4px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Background.Level1,
        border: `1px solid ${theme.vars.palette.primary.solid}`,
        animation: "approvalPop 0.3s ease",
        "@keyframes approvalPop": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.vars.palette.primary.solid,
            animation: "pulse 1.5s ease infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          level="body-xs"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: theme.vars.palette.primary.solid,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          승인 필요
        </Typography>
      </Stack>
      {data.action_requests.map((action, idx) => (
        <ApprovalItem
          key={idx}
          action={action}
          config={data.review_configs[idx]}
          onApprove={() => onDecide([{ type: "approve" }])}
          onEdit={(editedArgs) =>
            onDecide([
              {
                type: "edit",
                edited_action: { name: action.name, args: editedArgs },
              },
            ])
          }
          onReject={(message) => onDecide([{ type: "reject", message }])}
        />
      ))}
    </Stack>
  );
}

// args 의 top-level string field 중 JSON 객체/배열로 parse 되는 것은 inline 객체로
// 펼쳐서 보여준다. write_file.content 처럼 stringified JSON 을 그대로 escape 된 한 줄
// string 으로 보여주는 것을 막기 위함. 저장 시 inlinedKeys 로 다시 string 화한다.
function expandJsonStringFields(args: Record<string, unknown>): {
  displayText: string;
  inlinedKeys: string[];
} {
  const expanded: Record<string, unknown> = {};
  const inlinedKeys: string[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (parsed !== null && typeof parsed === "object") {
          expanded[key] = parsed;
          inlinedKeys.push(key);
          continue;
        }
      } catch {
        // not JSON — fall through
      }
    }
    expanded[key] = value;
  }
  return { displayText: JSON.stringify(expanded, null, 2), inlinedKeys };
}

function collapseJsonStringFields(
  edited: Record<string, unknown>,
  inlinedKeys: string[]
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...edited };
  for (const key of inlinedKeys) {
    const v = result[key];
    if (v !== undefined && typeof v !== "string") {
      result[key] = JSON.stringify(v, null, 2);
    }
  }
  return result;
}

function ApprovalItem({
  action,
  config,
  onApprove,
  onEdit,
  onReject,
}: {
  action: ActionRequest;
  config?: ReviewConfig;
  onApprove: () => void;
  onEdit: (args: Record<string, unknown>) => void;
  onReject: (message: string) => void;
}) {
  const [mode, setMode] = useState<"idle" | "edit" | "reject">("idle");
  const [{ argsText, inlinedKeys }, setArgsState] = useState(() => {
    const { displayText, inlinedKeys: keys } = expandJsonStringFields(
      action.args
    );
    return { argsText: displayText, inlinedKeys: keys };
  });
  const [rejectMessage, setRejectMessage] = useState("");

  const allowed = config?.allowed_decisions ?? ["approve", "edit", "reject"];

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
          color: theme.vars.palette.Content.Strong,
          fontWeight: 600,
        }}
      >
        {action.name}
        <Box
          component="span"
          sx={{
            ml: "8px",
            color: theme.vars.palette.Content.Medium,
            fontWeight: 400,
          }}
        >
          {typeof action.args.file_path === "string"
            ? String(action.args.file_path)
            : ""}
        </Box>
      </Box>

      <Typography
        level="body-sm"
        sx={{
          fontSize: "13px",
          color: theme.vars.palette.Content.Medium,
          lineHeight: 1.5,
        }}
      >
        {action.description}
      </Typography>

      <ActionArgsPreview args={action.args} />

      {mode === "idle" && (
        <Stack direction="row" spacing={1}>
          {allowed.includes("approve") && (
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={onApprove}
              sx={{ minHeight: "30px", fontSize: "12px", fontWeight: 600 }}
            >
              ✓ 승인
            </Button>
          )}
          {allowed.includes("edit") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("edit")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✎ 수정
            </Button>
          )}
          {allowed.includes("reject") && (
            <Button
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => setMode("reject")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✕ 거부
            </Button>
          )}
        </Stack>
      )}

      {mode === "edit" && (
        <Stack spacing={1}>
          <Textarea
            value={argsText}
            onChange={(e) =>
              setArgsState((prev) => ({
                ...prev,
                argsText: e.target.value,
              }))
            }
            minRows={6}
            maxRows={14}
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => {
                try {
                  const parsed = JSON.parse(argsText);
                  onEdit(collapseJsonStringFields(parsed, inlinedKeys));
                } catch {
                  // ignore parse error — leave UI open
                }
              }}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}

      {mode === "reject" && (
        <Stack spacing={1}>
          <Textarea
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="왜 거부하시나요? agent 에게 전달됩니다"
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "12px" }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="danger"
              onClick={() => onReject(rejectMessage.trim())}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              거부 전송
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function ActionArgsPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const isWriteFile = typeof args.content === "string";
  const isEditFile =
    typeof args.old_string === "string" && typeof args.new_string === "string";

  if (isEditFile) {
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} diff
        </Box>
        {expanded && (
          <Box
            sx={{
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                color: "#b91c1c",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
              }}
            >
              − {String(args.old_string)}
            </Box>
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(34, 197, 94, 0.08)",
                color: "#15803d",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              + {String(args.new_string)}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (isWriteFile) {
    const content = String(args.content);
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} content ({content.length} chars)
        </Box>
        {expanded && (
          <Box
            sx={{
              p: "8px 10px",
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              lineHeight: 1.5,
              maxHeight: "240px",
              overflow: "auto",
              whiteSpace: "pre",
            }}
          >
            {content}
          </Box>
        )}
      </Box>
    );
  }

  return null;
}
